import { computed, watch, type Ref } from 'vue';

interface UseEligibleUsersByPositionsOptions<TUser> {
  users: Readonly<Ref<TUser[]>>;
  selectedPositions: Readonly<Ref<string[]>>;
  selectedUsers: Ref<string[]>;
  getUserId: (user: TUser) => number | string | undefined;
  getUserPositionIds: (user: TUser) => number[];
  clearSelectedUsersWhenNoPositions?: boolean;
  canReconcile?: Readonly<Ref<boolean>>;
}

export function useEligibleUsersByPositions<TUser>({
  users,
  selectedPositions,
  selectedUsers,
  getUserId,
  getUserPositionIds,
  clearSelectedUsersWhenNoPositions = true,
  canReconcile,
}: UseEligibleUsersByPositionsOptions<TUser>) {
  const selectedPositionIds = computed(
    () =>
      new Set(
        selectedPositions.value
          .map((value) => Number(value))
          .filter((value) => !Number.isNaN(value))
      )
  );

  const eligibleUsers = computed(() => {
    if (selectedPositionIds.value.size === 0) {
      return [] as TUser[];
    }

    return users.value.filter((user) =>
      getUserPositionIds(user).some((positionId) => selectedPositionIds.value.has(positionId))
    );
  });

  const eligibleUserIds = computed(
    () =>
      new Set(eligibleUsers.value.map((user) => getUserId(user)?.toString() ?? '').filter(Boolean))
  );

  const reconcileSelectedUsers = () => {
    if (selectedPositionIds.value.size === 0) {
      if (clearSelectedUsersWhenNoPositions) {
        selectedUsers.value = [];
      }
      return;
    }

    if (canReconcile && !canReconcile.value) {
      return;
    }

    selectedUsers.value = selectedUsers.value.filter((userId) => eligibleUserIds.value.has(userId));
  };

  watch(
    [selectedPositions, users, ...(canReconcile ? [canReconcile] : [])],
    reconcileSelectedUsers,
    {
      immediate: true,
    }
  );

  return {
    eligibleUsers,
    eligibleUserIds,
    reconcileSelectedUsers,
  };
}
